import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import RideDetail from './pages/RideDetail';
import ClubDetail from './pages/ClubDetail';
import UserProfile from './pages/UserProfile';
import ResetPassword from './pages/ResetPassword';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import DownloadPage from './pages/DownloadPage';
import Footer from './components/Footer';
import InstallPrompt from './components/InstallPrompt';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from './components/ThemeProvider';
import PendingRatingsModal from './components/PendingRatingsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.baseURL = API;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [pendingRatings, setPendingRatings] = useState([]);
  const [showRatingModal, setShowRatingModal] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      checkPendingRatings();
    }
  }, [user]);

  const fetchCurrentUser = async () => {
    try {
      console.log('[App] Fetching current user...');
      console.log('[App] API baseURL:', axios.defaults.baseURL);
      console.log('[App] Token present:', !!token);
      
      const response = await axios.get('/auth/me');
      console.log('[App] SUCCESS - User fetched:', response.data?.email);
      setUser(response.data);
    } catch (error) {
      // VERBOSE PRODUCTION ERROR LOGGING - DO NOT REMOVE
      console.error('============================================');
      console.error('[App] CRITICAL ERROR - fetchCurrentUser FAILED');
      console.error('[App] Error name:', error.name);
      console.error('[App] Error message:', error.message);
      console.error('[App] Error code:', error.code);
      console.error('[App] Response status:', error.response?.status);
      console.error('[App] Response data:', JSON.stringify(error.response?.data));
      console.error('[App] This will log user out and redirect to /auth');
      console.error('============================================');
      
      localStorage.removeItem('token');
      setToken(null);
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const checkPendingRatings = async () => {
    try {
      const response = await axios.get('/notifications/pending-ratings');
      if (response.data.length > 0) {
        setPendingRatings(response.data);
        setShowRatingModal(true);
      }
    } catch (error) {
      console.error('Failed to check pending ratings');
    }
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    setToken(token);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <ThemeProvider defaultTheme="dark" storageKey="cycleconnect-theme">
      <div className="min-h-screen bg-background flex flex-col">
        <BrowserRouter>
          <div className="flex-1">
            <Routes>
              <Route
                path="/auth"
                element={!user ? <Auth onLogin={handleLogin} /> : <Navigate to="/" />}
              />
              <Route
                path="/"
                element={user ? <Dashboard user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} /> : <Navigate to="/auth" />}
              />
              <Route
                path="/ride/:rideId"
                element={user ? <RideDetail user={user} /> : <Navigate to="/auth" />}
              />
              <Route
                path="/club/:clubId"
                element={user ? <ClubDetail user={user} /> : <Navigate to="/auth" />}
              />
              <Route
                path="/profile/:userId"
                element={user ? <UserProfile /> : <Navigate to="/auth" />}
              />
              <Route
                path="/reset-password"
                element={<ResetPassword />}
              />
              <Route
                path="/terms"
                element={<Terms />}
              />
              <Route
                path="/privacy"
                element={<Privacy />}
              />
              <Route
                path="/download"
                element={<DownloadPage />}
              />
              <Route
                path="/get"
                element={<DownloadPage />}
              />
            </Routes>
          </div>
          <Footer />
        </BrowserRouter>
        <Toaster />
        
        {/* Pending Ratings Modal */}
        {showRatingModal && pendingRatings.length > 0 && (
          <PendingRatingsModal
            pendingRatings={pendingRatings}
            onClose={() => {
              setShowRatingModal(false);
              checkPendingRatings(); // Re-check after closing
            }}
            onComplete={() => {
              setShowRatingModal(false);
              setPendingRatings([]);
            }}
          />
        )}
        
        {/* PWA Install Prompt */}
        <InstallPrompt />
      </div>
    </ThemeProvider>
  );
}

export default App;
